import { useCallback, useEffect, useRef, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import debounce from 'lodash/debounce';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  findBundleProduct,
  patchBundleProduct,
} from '../../store/bundleProduct/api';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus as setBundleProductStatus,
} from '../../store/bundleProduct/bundleProductSlice';
import { BundleProduct } from '../../store/bundleProduct/api/interface';
import type { CreatePatchBundleProduct } from '../../store/bundleProduct/api/interface';
import Loader from '../../components/Loader';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { encryptString } from '../../utils/url';
import { findSelectBundleCategory } from '../../store/selectBundleCategory/api';
import BundleProductThumbnail from './BundleProductThumbnail';
import BundleProductPreviewImage from './BundleProductPreviewImage';
import BundleProductBulkImportModal from './BundleProductBulkImportModal';
import { getBundleListingActiveState } from './bundleProductStatus';
import { isBundleBulkImportEnabled } from '../../utils/bundleProductBulkImport';

const formatPrice = (price: string | number): string => {
  const value = Number(price);
  if (Number.isNaN(value)) return 'QAR 0';
  return Number.isInteger(value) ? `QAR ${value}` : `QAR ${value.toFixed(2)}`;
};

const getBundleImageKey = (item: BundleProduct) =>
  item.previewUrl || item.images?.[0]?.imageUrl || '';

const Index = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data, isLoading, totalRecords, limit, currentPage, skip } =
    useAppSelector((state) => state.bundleProduct);
  const categories = useAppSelector((state) => state.selectBundleCategory.data);
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const [search, setSearch] = useState<string>('');
  const [bundleCategoryId, setBundleCategoryId] = useState<string>('');
  const [activeStateFilter, setActiveStateFilter] = useState<string>('');
  const [orderedData, setOrderedData] = useState<BundleProduct[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const showBulkImport = isBundleBulkImportEnabled();

  const buildFindQuery = useCallback(
    () => ({
      limit,
      skip,
      ...(search && { search }),
      ...(bundleCategoryId && { bundleCategoryId }),
    }),
    [search, bundleCategoryId, limit, skip]
  );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setBundleProductStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const fetchProducts = useCallback(() => {
    dispatch(findBundleProduct(buildFindQuery()));
  }, [dispatch, buildFindQuery]);

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [previewImageKey, setPreviewImageKey] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');

  useEffect(() => {
    dispatch(findSelectBundleCategory({}));
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/bundle-product') {
      fetchProducts();
    }
  }, [location.pathname, location.state, fetchProducts]);

  useEffect(() => {
    let items = (data || []).map((item) => ({ ...item }));
    if (activeStateFilter !== '') {
      const wantActive = activeStateFilter === 'true';
      items = items.filter(
        (item) => getBundleListingActiveState(item) === wantActive
      );
    }
    setOrderedData(items);
  }, [data, activeStateFilter]);

  const handleSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    }, 700),
    []
  );

  const handleOpenLightBox = (imageKey: string, alt: string) => {
    setPreviewImageKey(imageKey);
    setPreviewAlt(alt);
    setOpenLightBox(true);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setPreviewImageKey('');
    setPreviewAlt('');
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return;
    }

    const items = Array.from(orderedData);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);
    setOrderedData(items);

    const positionUpdates = items
      .map((item, index) => ({
        item,
        position: skip + index + 1,
      }))
      .filter(({ item, position }) => Number(item.position) !== position);

    if (positionUpdates.length === 0) return;

    setIsReordering(true);
    try {
      await Promise.all(
        positionUpdates.map(({ item, position }) =>
          dispatch(
            patchBundleProduct({
              id: item.id,
              position,
            } as CreatePatchBundleProduct)
          ).unwrap()
        )
      );
      fetchProducts();
    } catch (error) {
      setOrderedData(data || []);
    } finally {
      setIsReordering(false);
    }
  };

  const getItemsCount = (item: BundleProduct): number =>
    item.itemsCount ?? 0;

  return (
    <Page title={'Bundle Products: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Bundle Products'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Bundle Products' },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {showBulkImport && (
                <Button
                  variant='outlined'
                  onClick={() => setBulkImportOpen(true)}
                  startIcon={<Iconify icon={'eva:upload-fill'} />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Bulk Import
                </Button>
              )}
              <Button
                variant='contained'
                component={RouterLink}
                to='/bundle-product/create'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                New Bundle Product
              </Button>
            </Stack>
          }
        />

        <Card>
          <Grid
            container
            spacing={2}
            alignItems='center'
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              md={3}
            >
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label='Category'
                  value={bundleCategoryId}
                  onChange={(e) => setBundleCategoryId(e.target.value)}
                >
                  <MenuItem value=''>All Categories</MenuItem>
                  {categories?.map((category) => (
                    <MenuItem
                      key={category.id}
                      value={category.id}
                    >
                      {category.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label='Status'
                  value={activeStateFilter}
                  onChange={(e) => setActiveStateFilter(e.target.value)}
                >
                  <MenuItem value=''>All Status</MenuItem>
                  <MenuItem value='true'>Active</MenuItem>
                  <MenuItem value='false'>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <TextField
                fullWidth
                onChange={handleSearch}
                placeholder='Search...'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  gap: 0.5,
                }}
              >
                <Iconify
                  icon={'mdi:drag-vertical'}
                  sx={{ width: 18, height: 18 }}
                />
                Drag rows to reorder
              </Typography>
            </Grid>
          </Grid>

          <Scrollbar>
            <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Table
                  size='small'
                  sx={{
                    minWidth: 1040,
                    width: '100%',
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': {
                      px: 1,
                      py: 1,
                      overflow: 'hidden',
                      verticalAlign: 'middle',
                    },
                    '& .MuiTableCell-head': {
                      '&:first-of-type': {
                        pl: 1,
                        boxShadow: 'none',
                      },
                      '&:last-of-type': {
                        pr: 1,
                        boxShadow: 'none',
                      },
                    },
                    '& .MuiTableCell-body': {
                      '&:first-of-type': {
                        pl: 1,
                      },
                      '&:last-of-type': {
                        pr: 1,
                      },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40, px: 0.5 }} />
                      <TableCell align='center' sx={{ width: 72 }}>
                        {'Image'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: '14%' }}>
                        {'Label EN'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: '14%' }}>
                        {'Label AR'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: '10%' }}>
                        {'Category'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 80 }}>
                        {'Items'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 88 }}>
                        {'Price'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 72 }}>
                        {'Position'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 100 }}>
                        {'Home Page'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 88 }}>
                        {'Status'}
                      </TableCell>
                      <TableCell align='center' sx={{ width: 64 }}>
                        {'Action'}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <Droppable droppableId='bundle-products'>
                    {(droppableProvided) => (
                      <TableBody
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        {isLoading || isReordering ? (
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              align='center'
                            >
                              <Loader />
                            </TableCell>
                          </TableRow>
                        ) : orderedData?.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={11}
                              align='center'
                            >
                              {'No record found'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          orderedData.map((item, index) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id}
                              index={index}
                            >
                              {(draggableProvided, snapshot) => (
                                <TableRow
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  sx={{
                                    bgcolor: snapshot.isDragging
                                      ? 'action.hover'
                                      : 'inherit',
                                  }}
                                >
                                  <TableCell
                                    {...draggableProvided.dragHandleProps}
                                    sx={{ width: 36, px: 0.5, cursor: 'grab' }}
                                  >
                                    <Iconify
                                      icon={'mdi:drag-vertical'}
                                      sx={{ color: 'text.disabled' }}
                                    />
                                  </TableCell>
                                  <TableCell align='center'>
                                    <BundleProductThumbnail
                                      imageKey={getBundleImageKey(item)}
                                      assetUrl={assetUrl}
                                      alt={item.labelEn}
                                      onClick={
                                        getBundleImageKey(item)
                                          ? () =>
                                              handleOpenLightBox(
                                                getBundleImageKey(item),
                                                item.labelEn
                                              )
                                          : undefined
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    align='center'
                                    sx={{
                                      maxWidth: 140,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={item.labelEn}
                                  >
                                    {item.labelEn}
                                  </TableCell>
                                  <TableCell
                                    align='center'
                                    sx={{
                                      maxWidth: 140,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={item.labelAr}
                                  >
                                    {item.labelAr}
                                  </TableCell>
                                  <TableCell align='center'>
                                    {item.bundle_category?.labelEn || '-'}
                                  </TableCell>
                                  <TableCell align='center'>
                                    <Box
                                      component='span'
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 10,
                                        bgcolor: (theme) =>
                                          alpha(theme.palette.warning.main, 0.16),
                                        color: 'warning.dark',
                                        fontSize: 12,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {`${getItemsCount(item)} items`}
                                    </Box>
                                  </TableCell>
                                  <TableCell align='center'>
                                    {formatPrice(item.price)}
                                  </TableCell>
                                  <TableCell align='center'>
                                    <Box
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        bgcolor: 'grey.300',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {item.position}
                                    </Box>
                                  </TableCell>
                                  <TableCell align='center'>
                                    <Label
                                      variant='ghost'
                                      color={
                                        item.showOnHomePage === true ||
                                        String(item.showOnHomePage) === 'true'
                                          ? 'success'
                                          : 'default'
                                      }
                                    >
                                      {item.showOnHomePage === true ||
                                      String(item.showOnHomePage) === 'true'
                                        ? 'Yes'
                                        : 'No'}
                                    </Label>
                                  </TableCell>
                                  <TableCell align='center'>
                                    {(() => {
                                      const listingActive =
                                        getBundleListingActiveState(item);
                                      return (
                                        <Label
                                          variant='ghost'
                                          color={
                                            listingActive ? 'success' : 'error'
                                          }
                                        >
                                          {listingActive ? 'Active' : 'InActive'}
                                        </Label>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell align='center'>
                                    <IconButton
                                      component={RouterLink}
                                      to={`/bundle-product/edit/${encryptString(item.id)}`}
                                      aria-label='edit'
                                    >
                                      <Iconify icon={'eva:edit-fill'} />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          ))
                        )}
                        {droppableProvided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </DragDropContext>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>

      {showBulkImport && (
        <BundleProductBulkImportModal
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          accessToken={accessToken}
          onImported={fetchProducts}
        />
      )}

      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {previewImageKey && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <BundleProductPreviewImage
                    imageKey={previewImageKey}
                    assetUrl={assetUrl}
                    alt={previewAlt}
                  />
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </Page>
  );
};

export default Index;
